import React, {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
  useCallback,
} from "react";
import {
  Autocomplete,
  CircularProgress,
  Checkbox,
  TextField,
  Chip,
} from "@mui/material";
import { CustomTextField, CustomFormLabel } from "../formsComponents";
import { api } from "../../api/api";
import { IMasterFoodMenu } from "../../redux/food/food.types";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useDebounce } from "../../hooks/useDebounce";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface IMasterFoodMenuAutocompleteProps {
  value?: IMasterFoodMenu[];
  disabled?: boolean;
  label?: string;
  onChange: (items: IMasterFoodMenu[]) => void;
  companyOutletUuid: string;
  searchOnType?: boolean;
}

export const MasterFoodMenuAutocomplete = forwardRef<
  {
    clear: () => void;
  },
  IMasterFoodMenuAutocompleteProps
>((props, ref) => {
  const {
    value = [],
    disabled,
    label,
    onChange,
    companyOutletUuid,
    searchOnType = true,
  } = props;
  const [options, setOptions] = useState<readonly IMasterFoodMenu[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState<string>("");
  const initialLoad = useRef(false);
  const [key, setKey] = React.useState(0);

  // Use the useDebounce hook
  const debouncedSearchText = useDebounce(searchText, 800);

  // Memoize the fetch function to prevent unnecessary re-renders
  const fetchSuggestion = useCallback(
    async (enteredValue: string = "") => {
      setLoading(true);
      try {
        let url = `/food/get-master-menu?company_outlet_uuid=${companyOutletUuid}`;

        if (enteredValue.length > 0) {
          const advanceFilter = {
            column: ["title"],
            operator: "CONTAINS",
            value: enteredValue,
            logicalOperator: "AND",
          };
          const encodedFilter = encodeURIComponent(
            JSON.stringify(advanceFilter)
          );
          url += `&advanceFilter=${encodedFilter}`;
        }

        const res = await api.get(url);
        const data: IMasterFoodMenu[] = res.data.data;
        setOptions(data);
      } catch (error) {
        console.error("Error fetching master menus:", error);
      } finally {
        setLoading(false);
      }
    },
    [companyOutletUuid]
  );

  // Case 1: searchOnType = true (default)
  // - Don't fetch initially
  // - Only fetch when user types (debounced)
  useEffect(() => {
    if (searchOnType) {
      if (debouncedSearchText.length > 0) {
        fetchSuggestion(debouncedSearchText);
      } else {
        // Clear options when search text is empty in searchOnType mode
        setOptions([]);
      }
    }
  }, [debouncedSearchText, searchOnType, fetchSuggestion]);

  // Case 2: searchOnType = false
  // - Fetch all data initially
  // - Also fetch when user types (immediate, no debounce needed for initial load)
  useEffect(() => {
    if (!searchOnType && companyOutletUuid) {
      // For initial load when searchOnType is false
      if (!initialLoad.current) {
        fetchSuggestion(""); // Fetch all data initially
        initialLoad.current = true;
      }
      // For subsequent searches when user types (in non-search mode)
      else if (searchText.length > 0) {
        fetchSuggestion(searchText); // Search immediately as user types
      } else if (searchText.length === 0) {
        fetchSuggestion(""); // Fetch all data again when search is cleared
      }
    }
  }, [searchText, searchOnType, companyOutletUuid, fetchSuggestion]);

  useImperativeHandle(ref, () => ({
    clear: () => {
      setSearchText("");
      setKey((prev) => prev + 1);
      onChange([]);
      if (!searchOnType) {
        fetchSuggestion(""); // Refresh the list when cleared in non-search mode
      } else {
        setOptions([]); // Clear options when in search mode
      }
    },
  }));

  const handleInputChange = (
    event: React.SyntheticEvent,
    newInputValue: string,
    reason: string
  ) => {
    // Only update search text for input events, not when selecting options
    if (reason === "input") {
      setSearchText(newInputValue);
    }
  };

  return (
    <>
      {label && <CustomFormLabel>{label}</CustomFormLabel>}
      <Autocomplete
        id="master-food-menu-autocomplete"
        fullWidth
        key={key}
        multiple
        sx={{
          ".MuiOutlinedInput-root": {
            paddingTop: "2px",
            paddingBottom: "2px",
            fontSize: "0.8rem",
            color: "rgb(38, 38, 38)",
            width: "100%",
          },
        }}
        getOptionLabel={(option) => option.title || ""}
        isOptionEqualToValue={(option, value) =>
          option.master_menu_uuid === value.master_menu_uuid
        }
        filterOptions={(x) => x}
        options={options}
        autoComplete
        value={value}
        loading={loading}
        disabled={disabled}
        noOptionsText={searchOnType ? "Type to search" : "No options available"}
        onChange={(event, newValue: IMasterFoodMenu[]) => {
          onChange(newValue);
        }}
        onInputChange={handleInputChange}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {option.title}
          </li>
        )}
        renderInput={(params) => (
          <CustomTextField
            {...params}
            fullWidth
            placeholder={
              searchOnType ? "Search master menus" : "Select master menus"
            }
            value={params.inputProps.value}  
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.master_menu_uuid}
              label={option.title}
              size="small"
            />
          ))
        }
      />
    </>
  );
});

MasterFoodMenuAutocomplete.displayName = "MasterFoodMenuAutocomplete";
